import { useState } from 'react'
import { Badge, Center, Group, Image, Loader, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck } from '@tabler/icons-react'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useAppTranslation } from 'hooks/useTranslation'
import type { ApplicationRow } from 'supabase/desk'
import { isAlreadyDecided } from './deskErrors'
import { DeskImageSource, deskImagePaths, publicImageUrl } from './deskImages'
import { showDeskError } from './RejectMenu'

const THUMB = 96

export type ImageStripProps = {
  application: Pick<ApplicationRow, 'id' | 'display_image_index' | 'desk_display_image_index' | 'display_image_reason'>
  rawItem: DeskImageSource | null | undefined
  /** After a successful swap, with the new image's public URL (the form's picture follows). */
  onSwapped?: (url: string) => void
}

/** A stored 1-based index that points at an image, else image 1 (deskImages.shownImagePath). */
const validIndex = (index: number | null | undefined, count: number): number =>
  typeof index === 'number' && index >= 1 && index <= count ? index : 1

/**
 * The image strip (IMG-04, D-08, UI-SPEC §D "Image strip").
 *
 * One click on another thumbnail IS the swap: `desk_swap_image` writes
 * `desk_display_image_index` and `main_picture` at once, with no save step. The
 * ring moves optimistically, a loader sits on the clicked thumbnail until the
 * write settles, and an error puts the ring back. The editor's own pick
 * (`display_image_index`) is never changed and keeps its "Editor's pick" badge.
 *
 * The URL sent is built by `getPublicUrl` from the raw item's own stored path,
 * never by string concatenation or from anything typed (T-33-59); the RPC
 * checks the suffix and index again. `display_image_reason` is model text and
 * reaches the tooltip only as a plain string (T-33-56).
 */
export const ImageStrip = ({ application, rawItem, onSwapped }: ImageStripProps) => {
  const { t } = useAppTranslation()
  const { swapImage } = useDeskDecision()
  const paths = rawItem ? deskImagePaths(rawItem) : []

  // The ring before the server catches up. Kept after a success (the re-read
  // brings the same index), restored to its previous value on an error.
  const [optimistic, setOptimistic] = useState<number | null>(null)
  const [pending, setPending] = useState<number | null>(null)

  if (paths.length === 0) return null

  if (paths.length === 1) {
    return (
      <Image
        src={publicImageUrl(paths[0])}
        alt={t('desk.images.shown')}
        w='100%'
        mah={240}
        fit='contain'
        radius='sm'
      />
    )
  }

  const serverShown = validIndex(
    application.desk_display_image_index ?? application.display_image_index,
    paths.length
  )
  const shown = optimistic ?? serverShown
  const editorPick = application.display_image_index
  const reason = application.display_image_reason?.trim() ? application.display_image_reason : null

  const choose = async (index: number) => {
    if (index === shown || pending !== null) return
    const previous = optimistic
    const mainPicture = publicImageUrl(paths[index - 1])
    setOptimistic(index)
    setPending(index)
    try {
      await swapImage.mutateAsync({ id: application.id, index, mainPicture })
    } catch (error) {
      setOptimistic(previous)
      setPending(null)
      if (isAlreadyDecided(error)) {
        showDeskError(t, error)
      } else {
        notifications.show({ color: 'red', message: t('desk.images.change_failed') })
      }
      return
    }
    setPending(null)
    notifications.show({ color: 'green', message: t('desk.images.changed'), autoClose: 3000 })
    onSwapped?.(mainPicture)
  }

  return (
    <Stack gap={8}>
      <Text size='sm' fw={600}>
        {t('desk.images.title')}
      </Text>
      <Group gap={8} align='flex-start'>
        {paths.map((path, i) => {
          const n = i + 1
          const isShown = n === shown
          const isPending = n === pending
          return (
            <Stack key={`${n}-${path}`} gap={4} align='center' data-image-slot={n}>
              <UnstyledButton
                aria-label={t('desk.images.use', { n })}
                aria-pressed={isShown}
                aria-busy={isPending || undefined}
                onClick={() => choose(n)}
                style={{
                  width: THUMB,
                  height: THUMB,
                  position: 'relative',
                  borderRadius: 4,
                  boxShadow: isShown ? '0 0 0 2px var(--mantine-color-blue-6)' : undefined
                }}
              >
                <Image src={publicImageUrl(path)} alt='' w={THUMB} h={THUMB} fit='cover' radius='sm' />
                {isShown && (
                  <Center
                    role='img'
                    aria-label={t('desk.images.shown')}
                    bg='blue.6'
                    c='white'
                    style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%' }}
                  >
                    <IconCheck size={16} />
                  </Center>
                )}
                {isPending && (
                  <Center style={{ position: 'absolute', inset: 0 }}>
                    <Loader size='xs' />
                  </Center>
                )}
              </UnstyledButton>
              {n === editorPick && (
                <Tooltip label={reason ?? ''} disabled={!reason} multiline maw={280}>
                  <Badge size='sm' fz={12} variant='light' color='gray'>
                    {t('desk.images.editor_pick')}
                  </Badge>
                </Tooltip>
              )}
            </Stack>
          )
        })}
      </Group>
    </Stack>
  )
}
